import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, ShieldCheck, UserRound } from "lucide-react";
import { ROLE_LABELS, type Role } from "@academichub/shared";
import { ApiError } from "../api/client";
import { useAuth } from "../hooks/useAuth";
import { LoginInfo } from "../components/LoginInfo";
import { Alert, Button, Card, Field, Input } from "../components/ui";

interface DemoAccount {
  username: string;
  password: string;
  role: Role;
  note: string;
}

/**
 * Kredensial seed untuk keperluan demo (lihat .env: SEED_ADMIN_PASSWORD /
 * SEED_USER_PASSWORD). Ganti kata sandi sebelum dipakai di luar demo.
 */
const ADMIN_PASSWORD = "Admin12345!";
const USER_PASSWORD = "User123456!";

const DEMO_ACCOUNTS: DemoAccount[] = [
  {
    username: "admin.institut",
    password: ADMIN_PASSWORD,
    role: "INSTITUT_ADMIN",
    note: "Seluruh institusi",
  },
  {
    username: "admin.fstt",
    password: ADMIN_PASSWORD,
    role: "FAKULTAS_ADMIN",
    note: "Fakultas Sains Terapan dan Teknologi",
  },
  {
    username: "admin.farmasi",
    password: ADMIN_PASSWORD,
    role: "FAKULTAS_ADMIN",
    note: "Fakultas Farmasi",
  },
  {
    username: "admin.prodi.ti",
    password: ADMIN_PASSWORD,
    role: "PRODI_ADMIN",
    note: "Prodi S1 Teknik Informatika",
  },
  {
    username: "dosen.ti",
    password: USER_PASSWORD,
    role: "DOSEN",
    note: "Prodi S1 Teknik Informatika",
  },
  {
    username: "mhs.ti",
    password: USER_PASSWORD,
    role: "MAHASISWA",
    note: "Prodi S1 Teknik Informatika",
  },
  {
    username: "admin.prodi.si",
    password: ADMIN_PASSWORD,
    role: "PRODI_ADMIN",
    note: "Prodi S1 Sistem Informasi",
  },
  {
    username: "dosen.si",
    password: USER_PASSWORD,
    role: "DOSEN",
    note: "Prodi S1 Sistem Informasi",
  },
  {
    username: "admin.prodi.farmasi",
    password: ADMIN_PASSWORD,
    role: "PRODI_ADMIN",
    note: "Prodi S1 Farmasi",
  },
  {
    username: "dosen.farmasi",
    password: USER_PASSWORD,
    role: "DOSEN",
    note: "Prodi S1 Farmasi",
  },
  {
    username: "mhs.farmasi",
    password: USER_PASSWORD,
    role: "MAHASISWA",
    note: "Prodi S1 Farmasi",
  },
  {
    username: "operator.lab.farmasi",
    password: USER_PASSWORD,
    role: "UNIT_OPERATOR",
    note: "Lab. Farmasi",
  },
];

function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Tidak dapat masuk. Periksa koneksi lalu coba lagi.";
}

const SSO_MESSAGES: Record<string, string> = {
  no_account:
    "Email akun Zetri Anda belum tertaut ke akun ISTN Activity Hub. Masuk dengan username dan kata sandi, lalu minta admin menautkannya.",
  email_unverified: "Email akun Zetri Anda belum terverifikasi.",
  inactive: "Akun Anda dinonaktifkan.",
  pending: "Pendaftaran akun Anda masih menunggu persetujuan admin program studi.",
  rejected: "Pendaftaran akun Anda ditolak.",
  link_conflict: "Akun Zetri ini sudah tertaut ke pengguna lain.",
  state_invalid: "Sesi login SSO kedaluwarsa. Silakan coba lagi.",
  disabled: "Login SSO belum diaktifkan.",
  access_denied: "Anda membatalkan proses masuk lewat Zetri SSO.",
};

export function LoginPage() {
  const { login, isAuthed } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);

  const ssoReason = params.get("sso");
  const ssoMessage = ssoReason
    ? (SSO_MESSAGES[ssoReason] ?? "Login lewat Zetri SSO gagal. Silakan coba lagi.")
    : null;

  useEffect(() => {
    if (isAuthed) navigate("/", { replace: true });
  }, [isAuthed, navigate]);

  useEffect(() => {
    fetch("/api/v1/auth/sso/status")
      .then((response) => (response.ok ? response.json() : { enabled: false }))
      .then((data: { enabled?: boolean }) => setSsoEnabled(Boolean(data.enabled)))
      .catch(() => setSsoEnabled(false));
  }, []);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login({ username, password });
      navigate("/", { replace: true });
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-dvh bg-slate-100 px-4 py-10">
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <header className="space-y-1 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700 ring-1 ring-brand-200">
            <ShieldCheck className="size-4" aria-hidden />
            ISTN
          </span>
          <h1 className="text-2xl font-bold text-slate-900">ISTN Activity Hub</h1>
          <p className="text-sm text-slate-600">
            Pencatatan, kurasi, dan validasi kegiatan beserta bukti dukungnya.
          </p>
        </header>

        <div className="grid items-start gap-6 lg:grid-cols-2">
          <div className="space-y-5 lg:sticky lg:top-10">
            <Card className="space-y-4">
              <form className="space-y-3" onSubmit={onSubmit}>
                <Field label="Username / Email / NIDN" htmlFor="username">
                  <Input
                    id="username"
                    name="username"
                    autoComplete="username"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    placeholder="mis. dosen.farmasi"
                    required
                  />
                </Field>

                <Field label="Kata sandi" htmlFor="password">
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                </Field>

                {error ? <Alert tone="error">{error}</Alert> : null}

                <Button type="submit" loading={busy} className="w-full">
                  <KeyRound className="size-4" aria-hidden />
                  Masuk
                </Button>

                {ssoMessage ? <Alert tone="error">{ssoMessage}</Alert> : null}

                {ssoEnabled ? (
                  <a
                    href="/api/v1/auth/sso/start"
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-brand-200 bg-brand-50 px-4 py-2 text-sm font-semibold text-brand-700 hover:bg-brand-100"
                  >
                    <ShieldCheck className="size-4" aria-hidden />
                    Masuk dengan Zetri SSO
                  </a>
                ) : null}

                <p className="text-center text-xs text-slate-500">
                  Belum punya akun?{" "}
                  <Link to="/register" className="font-semibold text-brand-700 underline">
                    Daftar di sini
                  </Link>{" "}
                  — akun baru dapat dipakai setelah disetujui admin program studi.
                </p>
              </form>
            </Card>

            <details className="rounded-2xl bg-white p-4 text-sm ring-1 ring-slate-200">
              <summary className="cursor-pointer font-semibold text-slate-700">
                Akun demo (isi otomatis)
              </summary>
              <ul className="mt-3 space-y-2">
                {DEMO_ACCOUNTS.map((account) => (
                  <li key={account.username}>
                    <button
                      type="button"
                      onClick={() => {
                        setUsername(account.username);
                        setPassword(account.password);
                        setError(null);
                      }}
                      className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left ring-1 ring-slate-200 transition hover:bg-slate-50"
                    >
                      <span className="flex items-center gap-2">
                        <UserRound className="size-4 text-slate-400" aria-hidden />
                        <span>
                          <span className="block font-medium text-slate-800">
                            {account.username}
                          </span>
                          <span className="block text-xs text-slate-500">{account.note}</span>
                        </span>
                      </span>
                      <span className="shrink-0 text-xs font-medium text-slate-500">
                        {ROLE_LABELS[account.role]}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-slate-500">
                Kredensial di atas berasal dari data seed untuk keperluan demonstrasi.
              </p>
            </details>
          </div>

          <LoginInfo />
        </div>

        <footer className="pb-2 text-center text-xs text-slate-500">
          Institut Sains dan Teknologi Nasional
        </footer>
      </div>
    </div>
  );
}
